import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Facility } from 'brain-data/model/facility.model';
import { CreateFacilityRequest } from 'brain-data/model/create-facility-request.model';
import { environment } from '../../../../environments/environment';

export interface UtilitySpendRankEntry {
  facilityId: number;
  facilityName: string;
  spend: number;
}

export interface UtilitySpendRank {
  utility: string;
  best: UtilitySpendRankEntry[];
  worst: UtilitySpendRankEntry[];
}

export interface UtilityPerformanceScore {
  utility: string;
  yoyChangePct: number | null;
  peerPercentile: number | null;
  peerDisplayText: string;
  selfScore: number | null;
  peerScore: number | null;
  combinedScore: number | null;
  combinedGrade: string | null;
}

export interface FacilityPerformanceSummary {
  facilityId: number;
  period: string | null;
  overallScore: number | null;
  overallGrade: string | null;
  statusLabel: string | null;
  peerCount: number;
  peerConfidence: string;
  improvedCount: number;
  scoredCount: number;
  areaOfFocus: string | null;
  unavailableReason: string | null;
  utilities: UtilityPerformanceScore[];
}

export interface GroupUtilityScore {
  utility: string;
  score: number | null;
  grade: string | null;
  peerPercentile: number | null;
  peerDisplayText: string;
  yoyChangePct: number | null;
  memberCount: number;
}

export interface GroupFacilityScore {
  facilityId: number;
  overallScore: number | null;
  overallGrade: string | null;
  statusLabel: string | null;
}

export interface GroupPerformanceSummary {
  groupId: number;
  period: string | null;
  facilityCount: number;
  overallScore: number | null;
  overallGrade: string | null;
  statusLabel: string | null;
  improvedCount: number;
  scoredCount: number;
  utilities: GroupUtilityScore[];
  facilities: GroupFacilityScore[];
  mapCenterLatitude?: number | null;
  mapCenterLongitude?: number | null;
  mapZoom?: number | null;
}

export type FacilityAlertUtility = 'electricity' | 'gas' | 'water' | 'solar' | 'propane';

export interface FacilityAlert {
  id: string | number;
  alertType: string;
  title: string;
  detail: string;
  utility: FacilityAlertUtility | null;
  openedAt: string;
}

export interface FacilityAlerts {
  facilityId: number;
  openCount: number;
  alerts: FacilityAlert[];
}

export const FACILITIES_URL = `${environment.apiBaseUrl}/facilities`;
export const FACILITIES_ADMIN_URL = `${environment.apiBaseAdminUrl}/facilities`;

@Injectable({
  providedIn: 'root',
})
export class FacilityService {
  constructor(private http: HttpClient) {}

  getFacilities(): Observable<Facility[]> {
    return this.http.get<Facility[]>(FACILITIES_URL);
  }

  getFacilityById(facilityId?: number): Observable<Facility> {
    return this.http.get<Facility>(`${FACILITIES_URL}/${facilityId}`);
  }

  getPerformanceSummary(facilityId: number): Observable<FacilityPerformanceSummary> {
    return this.http.get<FacilityPerformanceSummary>(`${FACILITIES_URL}/${facilityId}/performance-summary`);
  }

  getGroupPerformanceSummary(groupId: number): Observable<GroupPerformanceSummary> {
    return this.http.get<GroupPerformanceSummary>(`${environment.apiBaseUrl}/facilityGroups/${groupId}/performance-summary`);
  }

  downloadPerformanceSummary(facilityId: number): Observable<Blob> {
    return this.http.get(`${FACILITIES_URL}/${facilityId}/performance-summary.pdf`, { responseType: 'blob' });
  }

  downloadGroupPerformanceSummary(groupId: number): Observable<Blob> {
    return this.http.get(`${environment.apiBaseUrl}/facilityGroups/${groupId}/performance-summary.pdf`, { responseType: 'blob' });
  }

  getUtilitySpendRank(groupId: number, utility: string, from: string, to: string): Observable<UtilitySpendRank> {
    const params = new HttpParams().set('from', from).set('to', to);
    return this.http.get<UtilitySpendRank>(`${environment.apiBaseUrl}/facilityGroups/${groupId}/utilities/${utility}/spend-rank`, { params });
  }

  getFacilityAlerts(facilityId: number): Observable<FacilityAlerts> {
    return this.http.get<FacilityAlerts>(`${FACILITIES_URL}/${facilityId}/alerts`);
  }

  updateFacility(facilityId: number, facility: Facility): Observable<void> {
    return this.http.put<void>(`${FACILITIES_ADMIN_URL}/${facilityId}`, facility);
  }

  createFacility(request: CreateFacilityRequest): Observable<Facility> {
    return this.http.post<Facility>(FACILITIES_ADMIN_URL, request);
  }

  listFacilityGroups(facilityId: number): Observable<Array<{ id: number; name: string }>> {
    return this.http.get<Array<{ id: number; name: string }>>(`${FACILITIES_ADMIN_URL}/${facilityId}/groups`);
  }

  moveFacilityToGroup(
    facilityId: number,
    targetGroupId: number,
  ): Observable<{
    facilityId: number;
    targetGroupId: number;
    action: string;
    success: boolean;
    alreadyInTarget?: boolean;
    mainGroupRemovalForbidden?: boolean;
    message?: string;
    groups: Array<{ id: number; name: string }>;
  }> {
    return this.http.post<{
      facilityId: number;
      targetGroupId: number;
      action: string;
      success: boolean;
      alreadyInTarget?: boolean;
      mainGroupRemovalForbidden?: boolean;
      message?: string;
      groups: Array<{ id: number; name: string }>;
    }>(`${FACILITIES_ADMIN_URL}/${facilityId}/moveToGroup/${targetGroupId}`, {});
  }

  copyFacilityToGroup(
    facilityId: number,
    targetGroupId: number,
  ): Observable<{
    facilityId: number;
    targetGroupId: number;
    action: string;
    success: boolean;
    alreadyInTarget?: boolean;
    groups: Array<{ id: number; name: string }>;
  }> {
    return this.http.post<{
      facilityId: number;
      targetGroupId: number;
      action: string;
      success: boolean;
      alreadyInTarget?: boolean;
      groups: Array<{ id: number; name: string }>;
    }>(`${FACILITIES_ADMIN_URL}/${facilityId}/copyToGroup/${targetGroupId}`, {});
  }

  removeFacilityFromGroup(
    facilityId: number,
    groupId: number,
  ): Observable<{
    facilityId: number;
    targetGroupId: number;
    action: string;
    success: boolean;
    mainGroupRemovalForbidden?: boolean;
    message?: string;
    groups: Array<{ id: number; name: string }>;
  }> {
    return this.http.delete<{
      facilityId: number;
      targetGroupId: number;
      action: string;
      success: boolean;
      mainGroupRemovalForbidden?: boolean;
      message?: string;
      groups: Array<{ id: number; name: string }>;
    }>(`${FACILITIES_ADMIN_URL}/${facilityId}/groups/${groupId}`);
  }
}
